import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import {
  WHAiChatComponent,
  WHAiChatErrorDetail,
  WHAiChatLabels,
  WHAiChatMode,
  WHImgAuthHeaderModule,
  WHLoginDataService,
} from '@workheld/workheld-shared-lib';
import { AsyncPipe } from '@angular/common';
import { environment } from 'src/environments/environment';
import { WHAiPageContextService } from './wh-ai-page-context.service';
import { AI_SESSION_STORAGE_KEY, AiStore } from './wh-ai.store';

/**
 * Workheld AI panel in the app's right drawer. The chat itself (aiservice,
 * history, feedback, documents) is <wh-ai-chat-panel>; Flow adds the page
 * context, its texts and theme.
 *
 * The chat is a fixed panel exactly over the drawer (which still makes room
 * for the page), never inline: switching to full screen then just widens the
 * live panel, so messages re-wrap smoothly in both directions.
 */
@Component({
  selector: 'workheld-ai',
  standalone: true,
  imports: [WHAiChatComponent, WHImgAuthHeaderModule, AsyncPipe],
  styles: `
    :host {
      display: block;
      height: 100%;
      /* Same as .wh-ai-drawer in app.component.scss */
      --wh-ai-chat-width: 500px;
      /* Tenant colors (copied into --wh-ai-* by the AI button) */
      --wh-ai-chat-primary: var(--wh-ai-primary);
      --wh-ai-chat-accent: var(--wh-ai-accent);
      /* Flow's Material look: text, borders, shapes and font */
      --wh-ai-chat-text: var(--wh-ai-text);
      --wh-ai-chat-text-muted: rgb(0 0 0 / 0.6);
      --wh-ai-chat-border: var(--border-color-light);
      --wh-ai-chat-surface-alt: #f5f5f5;
      --wh-ai-chat-radius: 8px;
      --wh-ai-chat-control-radius: 4px;
      --wh-ai-chat-input-radius: 8px;
      --wh-ai-chat-font-family: Inter, sans-serif;
      --wh-ai-chat-header-height: 64px;
    }

    :host-context(.wh-ai-default-theme) {
      --wh-ai-chat-header-background: linear-gradient(
        118deg,
        var(--wh-ai-primary) 0%,
        var(--wh-ai-accent) 100%
      );
    }
  `,
  template: `
    <wh-ai-chat-panel
      data-cy="wh-ai-chat"
      [allowFullscreen]="true"
      [(mode)]="mode"
      [open]="true"
      (openChange)="!$event && aiStore.setIsAiActive(false)"
      [endpointConfig]="endpointConfig"
      [pageContext]="pageContextService.pageContext()"
      [selectionContext]="true"
      [labels]="labels()"
      [logo]="aiStore.assistantLogoUrl() | WHImgAuthHeader | async"
      [sessionStorageKey]="sessionStorageKey"
      (chatError)="onChatError($event)"
    />
  `,
})
export class WorkheldAiComponent {
  readonly aiStore = inject(AiStore);
  readonly pageContextService = inject(WHAiPageContextService);
  private readonly translateService = inject(TranslateService);
  private readonly loginDataService = inject(WHLoginDataService);

  private readonly lang = toSignal(this.translateService.onLangChange);
  private readonly activeUser = toSignal(this.loginDataService.activeUserDOM$);

  readonly endpointConfig = { apiUrl: environment.apiUrl };
  readonly sessionStorageKey = AI_SESSION_STORAGE_KEY;
  /** Starts as sidebar each time the panel opens. */
  readonly mode = signal<WHAiChatMode>('sidebar');

  /** Flow's own texts where they exist; the chat's defaults otherwise. */
  readonly labels = computed<Partial<WHAiChatLabels>>(() => {
    this.lang();
    const firstName = this.activeUser()?.firstName;
    const intro = this.translated('app.ai.intro.title');
    return {
      title: this.aiStore.assistantName(),
      emptyTitle: intro && firstName ? `${intro}, ${firstName}?` : intro,
      placeholder: this.translated('app.ai.askSomething.input.placeholder'),
      newChat: this.translated('app.ai.newChat.button.tooltip'),
      history: this.translated('app.ai.chats.title'),
      historyEmpty: this.translated('app.ai.noSessionsAvailable.label'),
      untitledChat: this.translated('app.ai.newSession.button.label'),
      close: this.translated('app.close.label'),
      sources: this.translated('app.ai.sources.title.label'),
    };
  });

  onChatError({ error }: WHAiChatErrorDetail): void {
    Sentry.captureException(error);
  }

  private translated(key: string): string | undefined {
    const value = this.translateService.instant(key);
    return typeof value === 'string' && value && value !== key
      ? value
      : undefined;
  }
}
